import { useImpersonator } from '@solinkcorp/authentication';
import { NavSidebarProvider } from '@solinkcorp/solink-ui/components/NavSidebar/NavSidebar';
import AppRoutes from 'components/AppRoutes';
import { CommonContext, VideoContext } from 'components/Contexts';
import DesktopHeader from 'components/NavigationFrame/DesktopHeader/Aspen/DesktopHeader';
import { type NavTabWithComponent } from 'components/NavigationFrame/menu';
import ThemeVariables from 'env/themeVariables';
import { type FC, useContext } from 'react';
import useFluxStore from 'src/hooks/useFluxStore';
import { LocationStore } from 'stores/LocationStore';
import 'styles/aspen-page-offsets.sass';
import FeaturesUtil from 'utils/FeaturesUtil';
import isShallowEqual from 'utils/isShallowEqual';
import AppSidebar from './AppSidebar';

export interface SidebarNavigationFrameProps {
  updateLiveToggle: (toggle: boolean) => void;
  onShowAllNotifications: () => void;
  inboxCount: number;
  navigation: NavTabWithComponent[];
  defaultPath: string;
}

/**
 * App shell for the sidebar navigation (Aspen redesign).
 *
 * Unlike the legacy frame this is a plain flex layout rather than a pixel-sized
 * CSS grid, because the sidebar changes width when it collapses and the grid
 * columns were computed in JS from the window size.
 *
 * `NavSidebarProvider` wraps `#vd-app` on purpose: the rail is in-flow flex,
 * `--desktopNavWidth` sync lives on the provider, and content must sit beside
 * it. ⌘B is shadcn's default toggle shortcut.
 *
 * The header is the Aspen copy with `hideRelocatedControls` set, so the
 * location filter, notifications and profile menu render in the sidebar instead
 * of appearing twice.
 */
const SidebarNavigationFrame: FC<SidebarNavigationFrameProps> = ({
  updateLiveToggle,
  onShowAllNotifications,
  inboxCount,
  navigation,
  defaultPath,
}) => {
  const isImpersonating = !!useImpersonator();
  const { isLiveToggleOn } = useContext(VideoContext);
  const { appState } = useContext(CommonContext);

  const { locations, locationsById } = useFluxStore(
    LocationStore,
    (store) => ({
      locations: store.getLocations(),
      locationsById: store.getLocationsById(),
    }),
    isShallowEqual,
  );

  const { desktopTopNavHeight } = ThemeVariables;
  const canAccessWeb = FeaturesUtil.canAccessWeb();
  const bannerOffsetPx = appState.bannerHeightPx;

  return (
    <NavSidebarProvider
      className="flex w-full"
      style={{
        height: `calc(100vh - ${bannerOffsetPx}px)`,
        // Agents Performance Dashboard Figma: nav uses `background/base` (same as page
        // canvas), not a lighter surface fill.
        ['--sidebar' as string]: 'var(--background-base)',
      }}
    >
      {canAccessWeb && (
        <AppSidebar
          tabs={navigation}
          inboxCount={inboxCount}
          onShowAllNotifications={onShowAllNotifications}
          topOffsetPx={bannerOffsetPx}
          onLiveToggle={updateLiveToggle}
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        {canAccessWeb && (
          <div
            // Paint the wrapper — DesktopHeader's Grid sets background: initial,
            // so a class on .desktop-date-bar alone does not show through.
            // Matches the rail's `--sidebar` so the two read as one piece of
            // chrome; background-base is a shade darker and, under .dark, drops
            // the header to the same Gray-950 as the page body.
            style={{
              height: desktopTopNavHeight,
              background: 'rgb(var(--background-base))',
              paddingRight: 20,
              boxSizing: 'border-box',
            }}
            className="aspen-date-bar z-[100] shrink-0"
          >
            <DesktopHeader
              hideRelocatedControls
              handleLiveToggle={updateLiveToggle}
              isImpersonating={isImpersonating}
              isLiveToggleOn={isLiveToggleOn}
              onShowAllNotifications={onShowAllNotifications}
              locations={locations}
              locationsById={locationsById}
            />
          </div>
        )}
        <div className="logged-in min-h-0 flex-1" id="vd-app">
          <AppRoutes userPermittedRoutes={navigation} defaultPath={defaultPath} />
        </div>
      </div>
    </NavSidebarProvider>
  );
};

export default SidebarNavigationFrame;
